import { config } from "@/config.js";
import axios from "axios";

/**
 * Statuses that mean "the proxy could not reach Oliver", not "Oliver said no".
 *
 * Oliver is served at /data/ on the CRM's own origin, so a restarting pod does
 * not fail the connection — the gateway in front answers for it. Treating that
 * as a real error is what would turn a 10-second restart into a failed message.
 * A plain 500 is deliberately absent: that is Oliver itself having run and
 * thrown, which retrying will not fix.
 */
export const isUpstreamUnavailable = (status) =>
  status === 408 || status === 502 || status === 503 || status === 504;

/**
 * OliverClient - Self-contained API client for Oliver backend
 *
 * Handles all communication with the Oliver API including:
 * - Conversations (CRUD)
 * - Messages (CRUD, streaming)
 * - File uploads (presigned URL flow)
 * - Audio transcription
 * - Title generation
 */
// A file's extension, from its name or, for a name without one (a pasted image), its type.
const extensionOf = (file) => {
  if (file.name.includes(".")) return file.name.split(".").pop();
  const subtype = (file.type || "").split("/")[1] || "";
  return { jpeg: "jpg", "svg+xml": "svg", plain: "txt" }[subtype] || subtype;
};

class OliverClient {
  constructor() {
    this.http = null;
    this.baseUrl = null;
    this.initialized = false;
    this.initPromise = null;
  }

  /**
   * Initialize the client (lazy initialization)
   * Derives Oliver URL from CRM config
   */
  async init() {
    if (this.initialized) return;

    // Prevent multiple simultaneous initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInit();
    await this.initPromise;
  }

  async _doInit() {
    try {
      const configResult = await config.loadConfig();
      // Derive Oliver URL from CRM URL: /api/v1/ -> /data/
      this.baseUrl = configResult.api.replace("/api/v1/", "/data/");
      this.http = axios.create({
        baseURL: this.baseUrl,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      // Auth interceptor - reuse ARSS_TOKEN from CRM auth
      this.http.interceptors.request.use((requestConfig) => {
        const token = localStorage.getItem("ARSS_TOKEN");
        if (token) {
          requestConfig.headers["Authorization"] = `Bearer ${token}`;
        }
        return requestConfig;
      });

      // Response interceptor for error handling
      this.http.interceptors.response.use(
        (response) => response,
        (error) => {
          // Handle 401 - token expired or invalid
          if (error.response?.status === 401) {
            console.error("Oliver API: Unauthorized - token may be expired");
          }
          return Promise.reject(error);
        }
      );

      this.initialized = true;
    } catch (error) {
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * Get current auth token
   */
  getToken() {
    return localStorage.getItem("ARSS_TOKEN");
  }

  /**
   * Get the base URL (useful for SSE connections)
   */
  async getBaseUrl() {
    await this.init();
    return this.baseUrl;
  }

  // ============ CONVERSATIONS ============

  /**
   * Get list of conversations
   * @param {Object} options - { skip: number, limit: number, title?: string }
   * @returns {Promise} - Array of ConversationWithLastMessageResponse
   */
  async getConversations({ skip = 0, limit = 50, title = "" } = {}) {
    await this.init();
    return this.http.get(
      `v1/conversations?skip=${skip}&limit=${limit}${
        title ? `&title=${title}` : ""
      }`
    );
  }

  /**
   * Create a new conversation
   * @param {Object} options - { title?: string, contextSummary?: string }
   * @returns {Promise} - ConversationResponse
   */
  async createConversation({ title = null, contextSummary = null } = {}) {
    await this.init();
    return this.http.post("v1/conversations", {
      title,
      context_summary: contextSummary,
    });
  }

  /**
   * Get a single conversation by ID
   * @param {string} conversationId
   * @returns {Promise} - ConversationResponse
   */
  async getConversation(conversationId) {
    await this.init();
    return this.http.get(`v1/conversations/${conversationId}`);
  }

  /**
   * Update a conversation
   * @param {string} conversationId
   * @param {Object} options - { title?: string, contextSummary?: string }
   * @returns {Promise} - ConversationResponse
   */
  async updateConversation(conversationId, { title, contextSummary }) {
    await this.init();
    return this.http.put(`v1/conversations/${conversationId}`, {
      title,
      context_summary: contextSummary,
    });
  }

  /**
   * Soft delete a conversation
   * @param {string} conversationId
   * @returns {Promise}
   */
  async deleteConversation(conversationId) {
    await this.init();
    return this.http.delete(`v1/conversations/${conversationId}`);
  }

  /**
   * Permanently delete a conversation
   * @param {string} conversationId
   * @returns {Promise}
   */
  async permanentDeleteConversation(conversationId) {
    await this.init();
    return this.http.delete(`v1/conversations/${conversationId}/permanent`);
  }

  /**
   * Get or create the last conversation (useful for quick start)
   * @returns {Promise} - ConversationResponse
   */
  async getOrCreateConversation() {
    await this.init();
    return this.http.post("v1/conversations/last-or-new");
  }

  // ============ MESSAGES ============

  /**
   * Whether Oliver is currently generating a reply for this conversation.
   * Authoritative — a reply generates on the server whether or not anyone is
   * connected, so this is the only way a reopened tab can know.
   */
  async getGenerationStatus(conversationId) {
    await this.init();
    return this.http.get(`v1/conversations/${conversationId}/generation`);
  }

  /**
   * Get messages for a conversation with cursor-based pagination
   * @param {string} conversationId
   * @param {Object} options - { limit?: number, cursor?: string }
   * @returns {Promise} - PaginatedMessagesResponse
   */
  async getMessages(conversationId, { limit = 10, cursor = null } = {}) {
    await this.init();
    let url = `v1/conversations/${conversationId}/messages?limit=${limit}`;
    if (cursor) url += `&cursor=${cursor}`;
    return this.http.get(url);
  }

  /**
   * Create a new message in a conversation
   * @param {string} conversationId
   * @param {Object} options - { content, role?, mode?, contextMetadata?, webAccess?, attachmentIds?, attachments? }
   * attachmentIds takes priority; never send both.
   * @returns {Promise} - MessageResponse
   */
  async createMessage(
    conversationId,
    {
      content,
      role = "user",
      mode,
      contextMetadata,
      webAccess,
      attachmentIds,
      attachments,
    }
  ) {
    await this.init();
    const body = {
      content,
      role,
      mode,
      context_metadata: contextMetadata,
      web_access: webAccess,
    };
    if (attachmentIds?.length) {
      body.attachment_ids = attachmentIds;
    } else if (attachments?.length) {
      body.attachments = attachments;
    }
    return this.http.post(`v1/conversations/${conversationId}/messages`, body);
  }

  /**
   * Get a single message by ID
   * @param {string} conversationId
   * @param {string} messageId
   * @returns {Promise} - MessageResponse
   */
  async getMessage(conversationId, messageId) {
    await this.init();
    return this.http.get(
      `v1/conversations/${conversationId}/messages/${messageId}`
    );
  }

  /**
   * Update a message
   * @param {string} conversationId
   * @param {string} messageId
   * @param {Object} options - { content?, role?, mode?, contextMetadata?, webAccess? }
   * @returns {Promise} - MessageResponse
   */
  async updateMessage(
    conversationId,
    messageId,
    { content, role, mode, contextMetadata, webAccess }
  ) {
    await this.init();
    return this.http.put(
      `v1/conversations/${conversationId}/messages/${messageId}`,
      {
        content,
        role,
        mode,
        context_metadata: contextMetadata,
        web_access: webAccess,
      }
    );
  }

  /**
   * Delete a message
   * @param {string} conversationId
   * @param {string} messageId
   * @returns {Promise}
   */
  async deleteMessage(conversationId, messageId) {
    await this.init();
    return this.http.delete(
      `v1/conversations/${conversationId}/messages/${messageId}`
    );
  }

  /**
   * Submit (create or update) negative feedback on an assistant message.
   * Idempotent upsert: re-submitting updates the existing record.
   * @param {string} conversationId
   * @param {string} messageId
   * @param {Object} options - { reasons, comment?, rating? }
   * @returns {Promise} - FeedbackResponse
   */
  async submitMessageFeedback(
    conversationId,
    messageId,
    { reasons, comment, rating = "negative" }
  ) {
    await this.init();
    return this.http.put(
      `v1/conversations/${conversationId}/messages/${messageId}/feedback`,
      { reasons, comment, rating }
    );
  }

  /**
   * Remove (undo) the user's feedback on an assistant message.
   * @param {string} conversationId
   * @param {string} messageId
   * @returns {Promise}
   */
  async removeMessageFeedback(conversationId, messageId) {
    await this.init();
    return this.http.delete(
      `v1/conversations/${conversationId}/messages/${messageId}/feedback`
    );
  }

  /**
   * Get non-streaming answer for a message
   * @param {string} conversationId
   * @param {string} messageId
   * @returns {Promise} - MessageResponse
   */
  async getMessageAnswer(conversationId, messageId) {
    await this.init();
    return this.http.post(
      `v1/conversations/${conversationId}/messages/${messageId}/answer`
    );
  }

  // ============ STREAMING ============

  /**
   * Stream AI answer for a message using SSE
   * @param {string} conversationId
   * @param {string} messageId
   * @param {Object} callbacks - { onMetadata?, onContent?, onComplete?, onError?, onInteraction?, onActivityStep?, onArtifacts?, onFollowUps?, onGuardrail?, onEnd? }
   *   onEnd runs once when a stream that received `complete` is over, however it ended.
   * @param {Object} [options] - { signal? } AbortSignal to cancel the stream
   * @returns {Promise} - Resolves when stream completes
   */
  async streamAnswer(
    conversationId,
    messageId,
    {
      onMetadata,
      onContent,
      onComplete,
      onError,
      onInteraction,
      onActivityStep,
      onArtifacts,
      onFollowUps,
      onGuardrail,
      onEnd,
    },
    { signal } = {}
  ) {
    await this.init();
    const token = this.getToken();
    let receivedComplete = false;

    try {
      const response = await fetch(
        `${this.baseUrl}v1/conversations/${conversationId}/messages/${messageId}/answer/stream`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      if (!response.ok) {
        // Shaped like an axios error (err.response.data.detail) because that is
        // what the rest of the app reads — and because the caller has to be able
        // to tell a 409 ("a reply is already generating") from a real failure.
        let data = null;
        try {
          data = await response.json();
        } catch (_) {
          // no JSON body — fall back to the status line below
        }
        const err = new Error(
          data?.detail || `HTTP ${response.status}: ${response.statusText}`
        );
        err.response = { status: response.status, data };
        // A gateway answering for a pod that is not up is lost contact, not a
        // refusal — the caller should wait for it rather than fail the turn.
        if (isUpstreamUnavailable(response.status)) {
          err.connectionLost = true;
        }
        throw err;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = null;
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;
          continue;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line === "") {
            currentEvent = null;
          } else if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:") && currentEvent) {
            try {
              const data = JSON.parse(line.slice(5).trim());

              switch (currentEvent) {
                case "metadata":
                  onMetadata?.(data);
                  break;
                case "activity_step":
                  onActivityStep?.(data);
                  break;
                case "artifacts":
                  onArtifacts?.(data);
                  break;
                case "follow_ups":
                  // Arrives after `complete`: the answer is already final.
                  onFollowUps?.(data);
                  break;
                case "content":
                  onContent?.(data);
                  break;
                case "interaction":
                  onInteraction?.(data);
                  break;
                case "guardrail":
                  // Guardrail refusal/redirect metadata (route card, mode).
                  // The refusal text itself still arrives via `content`, so
                  // clients without this handler keep working.
                  onGuardrail?.(data);
                  break;
                case "error":
                  console.warn("Stream error (non-fatal):", data.content);
                  break;
                case "complete":
                  receivedComplete = true;
                  onComplete?.(data);
                  break;
              }
            } catch (parseError) {
              console.warn("Failed to parse SSE data:", line, parseError);
            }
          }
        }
      }

      // The stream ended without saying so. The turn may still be running (or
      // already finished) on a server we simply lost contact with, so this is
      // flagged as lost contact rather than a failed answer.
      if (!receivedComplete) {
        const err = new Error("Stream ended without complete event");
        err.connectionLost = true;
        onError?.(err);
      }
    } catch (error) {
      // An AbortError means the stream was intentionally canceled (e.g. the
      // conversation was deleted) — not a failure, so don't surface it.
      if (error?.name === "AbortError") {
        return;
      }
      // Chips are made after `complete`; losing the connection then costs only them.
      if (receivedComplete) {
        return;
      }
      // No `response` means the request never got an answer at all — DNS, a
      // refused connection, a socket dropped mid-stream. A server that replied
      // with a status told us something, and is handled as a real error.
      if (!error?.response) {
        error.connectionLost = true;
      }
      console.error("Stream error:", error);
      onError?.(error);
    } finally {
      if (receivedComplete) onEnd?.();
    }
  }

  // ============ FILE UPLOAD ============

  /**
   * Create a chat attachment before its upload: the row and a presigned PUT URL. The id
   * exists from the start, so a message can name the file however far its upload has got.
   * @param {Object} options - { conversationId, file }
   * @returns {Promise} - { id, upload_url, attachment }
   */
  async createAttachment({ conversationId, file }) {
    await this.init();
    return this.http.post("v1/attachments", {
      conversation_id: conversationId,
      file_name: file.name,
      size: file.size,
      mime_type: file.type || undefined,
      extension: extensionOf(file),
    });
  }

  /**
   * PUT a file to its presigned URL, reporting progress. XMLHttpRequest, not fetch: fetch
   * reports no upload progress, and the global fetch interceptors would add an
   * Authorization header that breaks the S3 signature.
   * @param {string} url - the presigned PUT URL
   * @param {File} file
   * @param {Object} options - { contentType: the type the URL was signed with (it must match
   *   exactly, or S3 refuses the upload: a .dcm has no browser type at all), onProgress(fraction),
   *   signal (AbortSignal) }
   * @returns {Promise<void>} - rejects on a network error, a non-2xx status or an abort
   */
  putAttachment(url, file, { contentType, onProgress, signal } = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.setRequestHeader(
        "Content-Type",
        contentType || file.type || "application/octet-stream"
      );
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.onabort = () =>
        reject(new DOMException("Upload cancelled", "AbortError"));
      if (signal) {
        if (signal.aborted) {
          reject(new DOMException("Upload cancelled", "AbortError"));
          return;
        }
        signal.addEventListener("abort", () => xhr.abort(), { once: true });
      }
      xhr.send(file);
    });
  }

  /**
   * Tell the server the upload finished (or gave up). Most files are read inside this
   * call and come back ready.
   * @param {string} attachmentId
   * @param {boolean} uploaded
   * @returns {Promise} - AttachmentResponse
   */
  async completeAttachment(attachmentId, uploaded = true) {
    await this.init();
    return this.http.post(`v1/attachments/${attachmentId}/complete`, {
      uploaded,
    });
  }

  /**
   * Where each file has got to, in one call (at most 20 ids).
   * @param {string[]} attachmentIds
   * @returns {Promise} - [{ id, status, stage, pages_total, pages_done, partial_pages, error }]
   */
  async attachmentStatuses(attachmentIds) {
    await this.init();
    return this.http.get("v1/attachments/status", {
      params: { ids: attachmentIds.join(",") },
    });
  }

  /**
   * Read a failed attachment again (the server reads it in its work loop).
   * @param {string} attachmentId
   * @returns {Promise} - Updated AttachmentResponse
   */
  async retryAttachment(attachmentId) {
    await this.init();
    return this.http.post(`v1/attachments/${attachmentId}/retry`);
  }

  // ============ ATTACHMENTS ============

  /**
   * Get all attachments for a message
   * @param {string} messageId
   * @returns {Promise} - Array of AttachmentResponse
   */
  async getAttachments(messageId) {
    await this.init();
    return this.http.get(`v1/messages/${messageId}/attachments`);
  }

  /**
   * File extensions the backend can actually read.
   *
   * Asked rather than hardcoded: a type the picker accepts and nothing can open is a file
   * the clinician waits for and gets nothing from.
   * @returns {Promise<{ data: { extensions: string[] } }>}
   */
  async getSupportedAttachmentTypes() {
    await this.init();
    return this.http.get("v1/attachments/supported-types");
  }

  /**
   * Get a single attachment by ID
   * @param {string} attachmentId
   * @returns {Promise} - AttachmentResponse
   */
  async getAttachment(attachmentId) {
    await this.init();
    return this.http.get(`v1/attachments/${attachmentId}`);
  }

  /**
   * The rows of a tabular attachment, as the server parsed them
   * @param {string} attachmentId
   * @returns {Promise<{ data: { columns: { name: string, label: string, type: string }[], rows: (string|null)[][], total: number } }>}
   */
  async getAttachmentTable(attachmentId) {
    await this.init();
    return this.http.get(`v1/attachments/${attachmentId}/table`);
  }

  /**
   * Delete an attachment
   * @param {string} attachmentId
   * @returns {Promise}
   */
  async deleteAttachment(attachmentId) {
    await this.init();
    return this.http.delete(`v1/attachments/${attachmentId}`);
  }

  // ============ AUXILIARY SERVICES ============

  /**
   * Generate a title for a conversation

  * @param {string} conversationId - The conversation ID
   * @returns {Promise} - Response is a plain string (the title)
   */
  async generateTitle(conversationId) {
    await this.init();
    return this.http.post(`v1/conversations/${conversationId}/generate-title`);
  }

  /**
   * Transcribe audio file to text
   * @param {File|Blob} audioFile - The audio file to transcribe
   * @returns {Promise} - TranscriptionResponse { transcription, request_id, filename }
   */
  async transcribeAudio(audioFile) {
    await this.init();
    const formData = new FormData();
    formData.append("audio", audioFile);
    return this.http.post("v1/transcription/transcribe", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  // ============ HEALTH CHECKS ============

  /**
   * Check API health
   * @returns {Promise}
   */
  async healthCheck() {
    await this.init();
    return this.http.get("v1/health");
  }

  /**
   * Check database health
   * @returns {Promise}
   */
  async healthCheckDb() {
    await this.init();
    return this.http.get("v1/health/db");
  }
}

// Singleton instance
export const oliverClient = new OliverClient();
export default oliverClient;
